import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Pin4Input from './Pin4Input';
import { pin4 as dict } from 'config/dictionary';
import { PrimaryButton, SecondaryButton, ButtonArrowState, ButtonType } from '@huggg/react-components'
import styles from './pin4.module.scss';
import ProductCard from '../ProductCard/ProductCard';
import classnames from 'classnames';

const pattern = /^7[0-9]{9}$/;

Yup.addMethod(Yup.string, 'validPin4Number', function(message) {
  return this.test('valid-pin4-number', message, function(value) {
    const { path, createError } = this;

    return pattern.test(value) || createError({ path, message });
  });
});

const StepOne = ({ errors, touched, setTouched, onProceedClick }) => (
  <>
    <Pin4Input
      name="mobile"
      error={errors.mobile}
      touched={touched.mobile}
      label={dict.labels.mobile}
    />
    <div className={classnames(styles.buttons)}>
      <PrimaryButton
        className={styles.confirm}
        onClick={() => onProceedClick(setTouched)}
        data-test-id="pin4-button-proceed"
        label={dict.labels.proceed}
      />
    </div>
  </>
);

const StepTwo = ({ isSubmitting, errors, touched, onBackClick }) => (
  <>
    <Pin4Input
      name="mobileConfirmation"
      error={errors.mobileConfirmation}
      touched={touched.mobileConfirmation}
      label={dict.labels.confirm}
    />
    <div className={classnames(styles.buttons)}>
      <p className={styles.legal}>{dict.labels.legal}</p>
      <PrimaryButton
        className={styles.confirm}
        type={ButtonType.Submit}
        data-test-id="pin4-button-confirm"
        disabled={isSubmitting}
        label={dict.labels.submit}
      />
      <SecondaryButton
        className={styles.confirm}
        data-test-id="pin4-button-go-back"
        disabled={isSubmitting}
        onClick={() => onBackClick()}
        label="WRONG NUMBER?"
        arrow={ButtonArrowState.Backwards}
      />
    </div>
  </>
);

StepOne.propTypes = {
  errors: PropTypes.object,
  touched: PropTypes.object,
  setTouched: PropTypes.func,
  onProceedClick: PropTypes.func
};

StepTwo.propTypes = {
  errors: PropTypes.object,
  touched: PropTypes.object,
  isSubmitting: PropTypes.bool,
  onBackClick: PropTypes.func
};

const Pin4 = props => {
  const { onComplete, productDetails } = props;
  const [showConfirm, setShowConfirm] = useState(false);

  const validationSchema = Yup.object({
    mobile: Yup.string()
      .required(dict.errors.mobileRequired)
      .validPin4Number(dict.errors.mobileFormat),

    mobileConfirmation: Yup.string()
      .required(dict.errors.confirmationRequired)
      .oneOf([Yup.ref('mobile'), null], dict.errors.mustMatch)
  });

  const onProceedClick = async (setTouched) => {
    const errorMessages = await setTouched({ mobile: true });

    if (!errorMessages.mobile) {
      setShowConfirm(true);
    }
  };

  const onBackClick = async event => {
    setShowConfirm(false);
  };

  return (
    <>
      {productDetails && (
        <div className={styles.cardContainer}>
          <ProductCard {...productDetails} />
        </div>
      )}
      <Formik
        onSubmit={onComplete}
        validationSchema={validationSchema}
        initialValues={{ mobile: '', mobileConfirmation: '' }}
      >
        {formikProps => {
          const {
            touched,
            errors,
            handleSubmit,
            isSubmitting,
            setTouched
          } = formikProps;

          return (
            <Form onSubmit={handleSubmit} className={styles.form}>
              <div className="container">
                {showConfirm ? (
                  <StepTwo
                    {...{ isSubmitting, errors, touched, onBackClick }}
                  />
                ) : (
                  <StepOne
                    {...{ errors, touched, setTouched, onProceedClick }}
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

Pin4.propTypes = {
  onComplete: PropTypes.func,
  productDetails: PropTypes.object
};

export default Pin4;
